import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import pebsteelLogo from '../assets/pebsteel-logo.png';
import {
  HiChartBar,
  HiDocumentText,
  //HiCloudUpload,
  HiRefresh,
  HiUsers,
  HiUserGroup,
  HiFolderOpen,
  HiTag,
  HiCog,
  HiChevronLeft,
  HiChevronRight,
  HiLogout,
  HiSearch,
} from 'react-icons/hi';

const ROUTE_TITLES = {
  '/': 'Documents',
  '/documents': 'Documents',
  '/dashboard': 'Dashboard',
  '/sync': 'Sync Status',
  '/users': 'Users',
  '/sites': 'Site Permissions',
  '/sync-sources': 'Sync Sources',
  '/document-types': 'Document Types',
  '/settings': 'Settings',
};

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Detect iframe mode:
  //  - window.parent !== window: cách chuẩn
  //  - localStorage['_last_portal_token']: fallback — CrmTokenReceiver luôn set key này
  //    khi chạy trong iframe và exchange token thành công từ Portal
  const isInIframe = window.parent !== window
    || !!localStorage.getItem('_last_portal_token');

  // Read from URL immediately to prevent timing issues with CrmTokenReceiver
  const params = new URLSearchParams(window.location.search);
  const urlIsOpenSidebar = params.get('isOpenSidebar');
  const storedIsOpenSidebar = localStorage.getItem('crm_isOpenSidebar');
  
  const initialIsOpen = urlIsOpenSidebar === 'false'
    ? false
    : urlIsOpenSidebar === 'true'
      ? true
      : storedIsOpenSidebar === 'false'
        ? false
        : true;

  const [isSidebarOpen, setIsSidebarOpen] = useState(initialIsOpen);

  const pageTitle = location.pathname.startsWith('/documents/')
    ? 'Document Detail'
    : (ROUTE_TITLES[location.pathname] ?? 'Dashboard');

  const baseNavItems = [
    { label: 'Documents', path: '/documents', icon: HiDocumentText },
    { label: 'Dashboard', path: '/dashboard', icon: HiChartBar },
    // { label: 'Upload', path: '/upload', icon: HiCloudUpload },
    { label: 'Sync Status', path: '/sync', icon: HiRefresh },
  ];

  const navItems = [
    ...baseNavItems,
    ...(user?.is_superuser ? [
      { label: 'Users', path: '/users', icon: HiUsers },
      { label: 'Sites', path: '/sites', icon: HiUserGroup },
      { label: 'Sync Sources', path: '/sync-sources', icon: HiFolderOpen },
      { label: 'Document Types', path: '/document-types', icon: HiTag },
      { label: 'Settings', path: '/settings', icon: HiCog },
    ] : []),
  ];

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar - thu nhỏ khi nằm trong iframe (isSidebarOpen sẽ phụ thuộc localStorage) */}
        <aside
          className={`bg-white border-r border-slate-200 transition-all duration-300 flex flex-col ${isSidebarOpen ? 'w-64' : 'w-20'}`}
        >
          <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100">
            {isSidebarOpen ? (
              <img src={pebsteelLogo} alt="Pebsteel" className="h-8 object-contain" />
            ) : (
              <img src={pebsteelLogo} alt="Pebsteel" className="h-7 w-7 object-contain mx-auto" />
            )}

            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-1 rounded hover:bg-slate-100 text-slate-400"
            >
              {isSidebarOpen
                ? <HiChevronLeft className="w-5 h-5" />
                : <HiChevronRight className="w-5 h-5" />
              }
            </button>
          </div>

          <nav className="flex-1 py-4 flex flex-col gap-1 px-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${isActive
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`
                  }
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {isSidebarOpen && <span>{item.label}</span>}
                </NavLink>
              );
            })}
          </nav>

          {!isInIframe && (
            <div className="p-4 border-t border-slate-100">
              <div className={`flex items-center gap-3 ${!isSidebarOpen && 'justify-center'}`}>
                <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                  {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </div>
                {isSidebarOpen && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{user?.full_name || 'User'}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                )}
                {isSidebarOpen && (
                  <button
                    onClick={logout}
                    className="text-slate-400 hover:text-red-500 transition-colors"
                    title="Logout"
                  >
                    <HiLogout className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </aside>

      {/* Main Content */}
      {/* A flex column, not a plain scroller.
          Previously <main> scrolled and the page sat inside a height-less div,
          so a page asking for h-full resolved against `auto` and got nothing.
          Any page building a full-height layout — a table with a pinned header,
          for instance — silently fell back to growing with its content, and the
          pinned header had no scrolling ancestor to pin against.
          Now the height flows: main fills the viewport, the header takes its
          fixed band, and the content area below is a definite, scrollable box. */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 shrink-0 bg-white/70 backdrop-blur-md border-b border-slate-200 z-10 px-8 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-slate-800">{pageTitle}</h1>
          <div className="flex items-center gap-4">
            <div className="relative hidden md:block">
              <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Quick search..."
                className="pl-9 pr-4 py-1.5 bg-slate-100 border-none rounded-full text-sm focus:ring-2 focus:ring-blue-500/20 w-64 transition-all"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    navigate(`/documents?q=${e.target.value}`);
                  }
                }}
              />
            </div>
          </div>
        </header>

        {/* min-h-0 lets this flex child shrink below its content so its own
            scrollbar engages instead of pushing the layout taller. Pages that
            are not full-height scroll here exactly as they did before. */}
        <div className="flex-1 min-h-0 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
